import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Assets
import printIcon from "../../assets/printer_icon.png";
import archiveIcon from "../../assets/archive_icon.png";
import editIcon from "../../assets/edit_icon.png";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";

// Styles
import classes from "../merchant-pages/ViewInvoice.module.css";

// Utils
import { toMoney } from "../../utils/toMoney";
import { getCleanedInvoiceNumber } from "../../utils/getCleanedInvoiceNumber";
import { DataContext } from "../../utils/DataContext";
import useLanguage from "../../utils/useLanguage";
import { formatDate, toDate } from "../../utils/toDate";
import { UserAuth } from "../../utils/AuthContext";
import { onlyNums } from "../../utils/OnlyNums";

// Components
import CustomInput from "../../components/CustomInput";
import PDFUploader from "../../components/PDFUploader";
import CustomButton from "../../components/CustomButton";
import Modal from "../../components/Modal";
import Loader from "../../components/Loader";
import Dropdown from "../../components/Dropdown";
import AreYouSureModal from "../../components/AreYouSureModal";
import ErrorBox from "../../components/ErrorBox";
import ErrorMessage from "../../components/ErrorMessage";

export default function ViewNoCustomerContainer() {
  const navigate = useNavigate();
  const { t } = useTranslation("general");
  const { id } = useParams();
  const { currentDirection } = useLanguage();
  const { userData } = useContext(UserAuth);
  const {
    selectedSupplier,
    fetchInvoices,
    // loading,
    // setLoading,
    currentBalance,
    setCurrentBalance,
  } = useContext(DataContext);

  const pdfUploaderRef = useRef(null);

  const [data, setData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState("");

  const [showAreYouSure, setShowAreYouSure] = useState(false);

  const [tempCurrentBalance, setTempCurrentBalance] = useState({
    value: 0,
    containerId: 0,
  });
  const [newRecord, setNewRecord] = useState({});
  const [delivered, setDelivered] = useState({
    title: "notDelivered",
    value: false,
  });
  const [invoiceDocs, setInvoiceDocs] = useState([]);
  const [tempDocs, setTempDocs] = useState([]);
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getShippedContainer/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // if (response?.status === 200) {
      //   setLoading(false);
      // }

      const shippedContainer = response?.data?.shippedContainer;
      setData(shippedContainer);
      setDelivered(
        shippedContainer?.delivered
          ? { title: "delivered", value: true }
          : { title: "notDelivered", value: false }
      );
      setNewRecord(shippedContainer);

      setInvoiceDocs(shippedContainer?.document);

      // setLoading(false);
    } catch (error) {
      // setLoading(false);
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setError("");
    setEditMode(true);
    // setNewRecord(data[index]);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setError("");
    setNewRecord(data);
    setTempDocs([]);
    setShowAreYouSure(false);

    setDelivered(
      data?.delivered
        ? { title: "delivered", value: true }
        : { title: "notDelivered", value: false }
    );
  };

  const handleAttemptCancel = () => {
    const jsonData = JSON.stringify(data);
    const jsonTempData = JSON.stringify(newRecord);

    const isDeliveredChanged = data?.delivered === delivered?.value;

    const jsonInvoiceDocs = JSON.stringify(data?.document);
    const jsonInvoiceTempDocs = JSON.stringify([...invoiceDocs, ...tempDocs]);

    if (
      jsonData === jsonTempData &&
      isDeliveredChanged &&
      jsonInvoiceDocs === jsonInvoiceTempDocs
    ) {
      cancelEdit();
    } else {
      setShowAreYouSure(true);
    }
  };

  const handleChildUploadDocs = (shippedContainerId) => {
    if (pdfUploaderRef.current) {
      pdfUploaderRef.current.handleUploadDocs(shippedContainerId);
    }
  };

  const handleConfirmEdit = async () => {
    const token = userData?.token;
    const organizationId = userData?.organizationId;

    try {
      setLoadingModal(true);
      const updateShippedContainer = await axios.put(
        `${process.env.REACT_APP_URL}/updateShippedContainerWithoutC`,
        {
          // * Reduce payload size => Useless data
          shippedContainerId: newRecord?.id,
          expectedArrivalDate: newRecord?.expectedArrivalDate,
          finalDestination: newRecord?.finalDestination,
          shipper: newRecord?.shipper,
          material: newRecord?.material,
          numberOfBoxes: parseFloat(newRecord?.numberOfBoxes),
          exitDate: newRecord?.exitDate,
          notes: newRecord?.notes,
          dateOfEntry: newRecord?.dateOfEntry,
          shippingDate: newRecord?.shippingDate,
          permitsReceivingDate: newRecord?.permitsReceivingDate,
          measuringUnit: newRecord?.measuringUnit,
          amountForSupplier: newRecord?.amountForSupplier,
          abstractAmountForCustomer: newRecord?.abstractAmountForCustomer,
          finalAmountForCustomer: newRecord?.finalAmountForCustomer,
          unitPrice: parseFloat(newRecord?.unitPrice),
          updateCounter: newRecord?.updateCounter,
          delivered: delivered?.value,
          containerNumber: newRecord?.containerNumber,
          amountForCustomer: newRecord?.amountForCustomer,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "organization-Id": organizationId,
          },
        }
      );

      const theData = updateShippedContainer?.data?.updatedShippedContainer;

      // If no update occurred, log an error
      if (updateShippedContainer?.status === 201) {
        // Update the record and data with the new invoice information returned from the backend
        setNewRecord(theData);
        setData(theData);
        setLoadingModal(false);

        handleChildUploadDocs(id);
      }
    } catch (error) {
      if (error.response.status === 409) {
        setError("errorContainerWithSameNumber");
        return;
      }

      setError("errorUpdatingContainer");
    } finally {
      // Fetch the updated invoices list after the edit completes
      fetchInvoices(selectedSupplier?.id);
      setLoadingModal(false);
    }

    // Exit the edit mode after confirming the changes
    setEditMode(false);
  };

  const handleCloseArchiveModal = () => setShowArchiveModal(false);

  const handleArchive = async () => {
    const token = localStorage.getItem("token");
    const userId = parseInt(localStorage.getItem("userId"), 10);

    try {
      setLoadingModal(true);
      const archivedInvoice = await axios.delete(
        `${process.env.REACT_APP_URL}/archiveShippedContainer/${data?.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            "Supplier-Id": data?.supplierId,
          },
        }
      );

      if (!archivedInvoice?.data) {
        console.error(`archivedInvoice.error`, archivedInvoice.error);
      } else {
        setCurrentBalance((prev) => ({
          ...prev,
          value: archivedInvoice?.data?.newBalance?.value,
        }));
      }

      navigate(`/noCustomerContainers`);
    } catch (error) {
      console.error("Error updating invoice:", error);
      setError("Error updating invoice.");
    } finally {
      setLoadingModal(false);
      fetchInvoices(selectedSupplier?.id);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setError("");
    setNewRecord((prevRow) => {
      let updatedRow = { ...prevRow, [name]: value };
      return updatedRow;
    });
  };

  const handleChangeDelivered = (option) => {
    setDelivered(option);
  };

  const handleDateChange = (e) => {
    const fieldName = e.target.name;
    const date = e.target.value;
    setNewRecord((prevRecord) => ({
      ...prevRecord,
      [fieldName]: new Date(date),
    }));
  };

  return (
    <>
      <main className={classes.viewInvoicePage}>
        <div className="e-container">
          <div className="user-logout">
            {!editMode ? (
              <CustomButton
                title={t("back")}
                variant="blank"
                icon={currentDirection === "ltr" ? previousIcon : nextIcon}
                iconSize={14}
                onClick={() => navigate("/noCustomerContainers")}
              />
            ) : null}

            <div className={classes.user} style={{ marginTop: "25px" }}>
              <p className={classes.userTitle}>{`${t("user")}: `}</p>
              <p className={classes.userName}>
                {` ${localStorage.getItem("username")}`}
              </p>
            </div>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              {data?.archived && (
                <div className="error-message">{t("archivedInvoice")}</div>
              )}

              <section className={classes.section}>
                <h3 className={classes.invoiceDetailsTitle}>
                  {t("containerDetails")}
                </h3>
              </section>
              {editMode && error && <ErrorBox>{t(error)}</ErrorBox>}
              <section className={classes.section}>
                <div className={classes.col}>
                  <span className={classes.colTitle}>{t("createdBy")}: </span>
                  <span className={`${classes.boldText} ${classes.breakWord}`}>
                    {data?.user?.username || " - "}
                  </span>
                </div>
              </section>

              <section
                className={`${classes.section} ${classes.invoiceNumber}`}
              >
                <div className={classes.col}>
                  <span className={classes.colTitle}>
                    {t("invoiceNumber")}:{" "}
                  </span>
                  {editMode ? (
                    <>
                      <div className={`o-row u-width_full-width`}>
                        <CustomInput
                          name="invoiceNumber"
                          value={getCleanedInvoiceNumber(
                            newRecord?.multiContainersInvoice?.invoiceNumber
                          )}
                          onChange={() => null}
                          disabled
                          readOnly
                          flex={true}
                        />
                      </div>
                    </>
                  ) : (
                    <span
                      className={`${classes.boldText} ${classes.breakWord}`}
                    >
                      {data?.multiContainersInvoice?.invoiceNumber || " - "}
                    </span>
                  )}
                </div>
              </section>

              <section
                className={`${classes.section} ${classes.invoiceNumber}`}
              >
                <div className={classes.col}>
                  <span className={classes.colTitle}>
                    {t("containerNumber")}:{" "}
                  </span>
                  {editMode ? (
                    <>
                      <div className={`o-row u-width_full-width`}>
                        <CustomInput
                          name="containerNumber"
                          value={getCleanedInvoiceNumber(
                            newRecord?.containerNumber
                          )}
                          onChange={(e) => {
                            setNewRecord((prev) => ({
                              ...prev,
                              containerNumber: e.target.value,
                            }));
                          }}
                          readOnly
                          flex={true}
                        />
                      </div>
                    </>
                  ) : (
                    <span
                      className={`${classes.boldText} ${classes.breakWord}`}
                    >
                      {data?.containerNumber || " - "}
                    </span>
                  )}
                </div>
              </section>

              <section className={classes.sectionSpecial}>
                <div className={classes.sectionSpecialCol}>
                  <span className={classes.colTitle}>{t("supplier")}: </span>
                  <span
                    className={`${classes.sectionSpecialColText} ${classes.breakWord}`}
                  >
                    {data?.supplier?.name || " - "}
                  </span>
                </div>
              </section>

              <section className={classes.section}>
                <div className={classes.col}>
                  <span className={classes.colTitle}>{t("company")}: </span>
                  <span className={`${classes.boldText} ${classes.breakWord}`}>
                    {data?.subCompany?.name || " - "}
                  </span>
                </div>
              </section>

              <section className={classes.section}>
                <div className={classes.col}>
                  <span className={classes.colTitle}>
                    {t("expectedArrivalDate")}:
                  </span>
                  {editMode ? (
                    <div className={`o-row u-width_full-width`}>
                      <CustomInput
                        type="date"
                        name="expectedArrivalDate"
                        value={
                          newRecord?.expectedArrivalDate
                            ? formatDate(newRecord?.expectedArrivalDate)
                            : "Invalid Date"
                        }
                        onChange={(date) => handleDateChange(date)}
                        flex={true}
                      />
                    </div>
                  ) : (
                    <span
                      className={`${classes.boldText} ${classes.breakWord}`}
                    >
                      {formatDate(data?.expectedArrivalDate) || "Invalid Date"}
                    </span>
                  )}
                </div>
              </section>

              <section className={classes.sectionSpecial}>
                <div className={classes.sectionSpecialCol}>
                  <span className={classes.colTitle}>
                    {t("finalDestination")}:{" "}
                  </span>
                  {editMode ? (
                    <div className={`o-row u-width_full-width`}>
                      <CustomInput
                        name="finalDestination"
                        value={newRecord?.finalDestination}
                        onChange={handleInputChange}
                        flex={true}
                      />
                    </div>
                  ) : (
                    <span
                      className={`${classes.sectionSpecialColText} ${classes.breakWord}`}
                    >
                      {data?.finalDestination || " - "}
                    </span>
                  )}
                </div>

                <div className={classes.sectionSpecialCol}>
                  <span className={classes.colTitle}>{t("shipper")}: </span>
                  {editMode ? (
                    <div className={`o-row u-width_full-width   `}>
                      <CustomInput
                        name="shipper"
                        value={newRecord?.shipper}
                        onChange={handleInputChange}
                        flex={true}
                      />
                    </div>
                  ) : (
                    <span
                      className={`${classes.sectionSpecialColText} ${classes.breakWord}`}
                    >
                      {data?.shipper || " - "}
                    </span>
                  )}
                </div>
              </section>

              <section className={classes.sectionSpecial}>
                <div className={classes.sectionSpecialCol}>
                  <span className={classes.colTitle}>{t("material")}: </span>
                  {editMode ? (
                    <div className={`o-row u-width_full-width`}>
                      <CustomInput
                        name="material"
                        value={newRecord?.material}
                        onChange={handleInputChange}
                        flex={true}
                      />
                    </div>
                  ) : (
                    <span
                      className={`${classes.sectionSpecialColText} ${classes.breakWord}`}
                    >
                      {data?.material || " - "}
                    </span>
                  )}
                </div>
              </section>
              <section className={classes.sectionSpecial}>
                <div className={classes.sectionSpecialCol}>
                  <span className={classes.colTitle}>
                    {t("numberOfBoxes")}:{" "}
                  </span>
                  {editMode ? (
                    <div className={`o-row u-width_full-width`}>
                      <CustomInput
                        name="numberOfBoxes"
                        value={newRecord?.numberOfBoxes}
                        onChange={handleInputChange}
                        flex={true}
                        type="moneyV2"
                      />
                    </div>
                  ) : (
                    <span
                      className={`${classes.sectionSpecialColText} ${classes.breakWord}`}
                    >
                      {data?.numberOfBoxes || " - "}
                    </span>
                  )}
                </div>

                <div className={classes.sectionSpecialCol}>
                  <span className={classes.minWidth100}>
                    {t("measuringUnit")}:
                  </span>
                  {editMode ? (
                    <>
                      <div className={`o-row u-width_full-width`}>
                        <CustomInput
                          name="measuringUnit"
                          value={newRecord?.measuringUnit}
                          onChange={handleInputChange}
                          flex={true}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <span
                        className={`${classes.sectionSpecialColText} ${classes.breakWord}`}
                      >
                        {data?.measuringUnit || " - "}
                      </span>
                    </>
                  )}
                </div>
              </section>
              {/* <section className={classes.section}>
                <div className={classes.col}>
                  <span className={classes.colTitle}>{t("unitPrice")}: </span>
                  {editMode ? (
                    <>
                      <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
                        <CustomInput
                          name="unitPrice"
                          value={newRecord?.unitPrice}
                          onChange={handleInputChange}
                          flex={true}
                        />

                        <span className="t-bold_bold">
                          {data?.supplier?.currency}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <span
                        className={`${classes.sectionSpecialColText} ${classes.breakWord}`}
                      >
                        {data?.unitPrice || " - "}
                      </span>
                      <span className="t-bold_bold">
                        {data?.supplier?.currency}
                      </span>
                    </>
                  )}
                </div>
              </section> */}
              {/* <section className={classes.section}>
                <div className={classes.col}>
                  <span className={classes.colTitle}>
                    {t("amountForSupplier") + " :"}
                  </span>
                  {editMode ? (
                    <>
                      <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
                        <CustomInput
                          name="amountForSupplier"
                          value={newRecord?.amountForSupplier}
                          onKeyDown={(e) => onlyNums(e)}
                          onChange={handleInputChange}
                          type="money"
                          flex={true}
                        />
                        <span className="t-bold_bold">
                          {data?.supplier?.currency}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <span
                        className={`${classes.boldText} ${classes.breakWord}`}
                      >
                        {toMoney(data?.amountForSupplier) || " - "}
                      </span>
                      <span className="t-bold_bold">
                        {data?.supplier?.currency}
                      </span>
                    </>
                  )}
                </div>
              </section> */}

              {/* <section className={classes.section}>
                <div className={classes.col}>
                  <span className={classes.colTitle}>
                    {t("abstractAmountForCustomer") + " :"}
                  </span>
                  {editMode ? (
                    <>
                      <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
                        <CustomInput
                          name="abstractAmountForCustomer"
                          value={newRecord?.abstractAmountForCustomer}
                          onKeyDown={(e) => onlyNums(e)}
                          onChange={handleInputChange}
                          type="money"
                          flex={true}
                        />
                        <span className="t-bold_bold">
                          {data?.customer?.currency}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <span
                        className={`${classes.boldText} ${classes.breakWord}`}
                      >
                        {toMoney(data?.abstractAmountForCustomer) || " - "}
                      </span>
                      <span className="t-bold_bold">
                        {data?.customer?.currency}
                      </span>
                    </>
                  )}
                </div>
              </section> */}

              <section className={classes.sectionSpecial}>
                <div className={classes.sectionSpecialCol}>
                  <span className={classes.colTitle}>{t("exitDate")}: </span>
                  {editMode ? (
                    <div className={`o-row u-width_full-width`}>
                      <CustomInput
                        type="date"
                        name="exitDate"
                        value={
                          newRecord?.exitDate
                            ? formatDate(newRecord?.exitDate)
                            : "Invalid Date"
                        }
                        onChange={(date) => handleDateChange(date)}
                        flex={true}
                      />
                    </div>
                  ) : (
                    <span
                      className={`${classes.sectionSpecialColText} ${classes.breakWord}`}
                    >
                      {formatDate(data?.exitDate) || " - "}
                    </span>
                  )}
                </div>
              </section>

              <section className={classes.sectionSpecial}>
                <div className={classes.sectionSpecialCol}>
                  <span className={classes.colTitle}>{t("dateOfEntry")}: </span>
                  {editMode ? (
                    <div className={`o-row u-width_full-width`}>
                      <CustomInput
                        type="date"
                        name="dateOfEntry"
                        value={
                          newRecord?.dateOfEntry
                            ? formatDate(newRecord?.dateOfEntry)
                            : "Invalid Date"
                        }
                        onChange={(date) => handleDateChange(date)}
                        flex={true}
                      />
                    </div>
                  ) : (
                    <span
                      className={`${classes.sectionSpecialColText} ${classes.breakWord}`}
                    >
                      {formatDate(data?.dateOfEntry) || " - "}
                    </span>
                  )}
                </div>
              </section>

              <section className={classes.sectionSpecial}>
                <div className={classes.sectionSpecialCol}>
                  <span className={classes.colTitle}>
                    {t("shippingDate")}:{" "}
                  </span>
                  {editMode ? (
                    <div className={`o-row u-width_full-width`}>
                      <CustomInput
                        type="date"
                        name="shippingDate"
                        value={
                          newRecord?.shippingDate
                            ? formatDate(newRecord?.shippingDate)
                            : "Invalid Date"
                        }
                        onChange={(date) => handleDateChange(date)}
                        flex={true}
                      />
                    </div>
                  ) : (
                    <span
                      className={`${classes.sectionSpecialColText} ${classes.breakWord}`}
                    >
                      {formatDate(data?.shippingDate) || " - "}
                    </span>
                  )}
                </div>
              </section>
              <section className={classes.sectionSpecial}>
                <div className={classes.sectionSpecialCol}>
                  <span className={classes.colTitle}>
                    {t("permitsReceivingDate")}:
                  </span>
                  {editMode ? (
                    <div className={`o-row u-width_full-width`}>
                      <CustomInput
                        type="date"
                        name="permitsReceivingDate"
                        value={
                          newRecord?.permitsReceivingDate
                            ? formatDate(newRecord?.permitsReceivingDate)
                            : "Invalid Date"
                        }
                        onChange={(date) => handleDateChange(date)}
                        flex={true}
                      />
                    </div>
                  ) : (
                    <span
                      className={`${classes.sectionSpecialColText} ${classes.breakWord}`}
                    >
                      {formatDate(data?.permitsReceivingDate)}
                    </span>
                  )}
                </div>
              </section>

              <section className={classes.sectionSpecial}>
                <div className={classes.sectionSpecialCol}>
                  <span className={classes.colTitle}>
                    {t("deliveryStatus")}:{" "}
                  </span>
                  {editMode ? (
                    <div className="o-row u-width_full-width">
                      <Dropdown
                        options={[
                          { title: "delivered", value: true },
                          {
                            title: "notDelivered",
                            value: false,
                          },
                        ]}
                        selected={delivered}
                        setSelected={handleChangeDelivered}
                        flex
                      />
                    </div>
                  ) : (
                    <span
                      className={`${classes.sectionSpecialColText} ${classes.breakWord}`}
                    >
                      {t(data?.delivered ? t("delivered") : t("notDelivered"))}
                    </span>
                  )}
                </div>
              </section>

              <section className={classes.section}>
                <div className={classes.col}>
                  <span className={`${classes.colTitle} u-height_full-height`}>
                    {t("notes")}:{" "}
                  </span>
                  {editMode ? (
                    <div className={`o-row u-width_full-width`}>
                      <CustomInput
                        name="notes"
                        value={newRecord?.notes}
                        onChange={handleInputChange}
                        flex={true}
                        type="textarea"
                      />
                    </div>
                  ) : (
                    <span className={`${classes.breakWord}`}>
                      {data?.notes}
                    </span>
                  )}
                </div>
              </section>
              <PDFUploader
                ref={pdfUploaderRef}
                invoiceNumber={data?.multiContainersInvoice?.invoiceNumber}
                documents={Array.isArray(invoiceDocs) ? invoiceDocs : []}
                invoiceDocs={Array.isArray(invoiceDocs) ? invoiceDocs : []}
                setInvoiceDocs={setInvoiceDocs}
                tempDocs={Array.isArray(tempDocs) ? tempDocs : []}
                setTempDocs={setTempDocs}
                mode={!editMode && "show"}
                invoiceType="shippedContainer"
              />
            
              <section className={classes.actions}>
                {editMode ? (
                  <div className="o-column">
                    {loadingModal ? <Loader /> : null}
                    {editMode && error && <ErrorMessage>{t(error)}</ErrorMessage>}
                    <div className={classes.actions}>
                      <CustomButton
                        title={t("confirm")}
                        onClick={handleConfirmEdit}
                        icon={saveIcon}
                        iconSize={16}
                        disabled={loadingModal}
                      />
                      <CustomButton
                        title={t("cancel")}
                        onClick={handleAttemptCancel}
                        variant="blank"
                        icon={closeIcon}
                        iconSize={14}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <CustomButton
                      variant="blank"
                      title={t("edit")}
                      onClick={handleEdit}
                      icon={editIcon}
                      iconSize={18}
                    />
                    {setEditMode ? (
                      <>
                        <CustomButton
                          title={t("print")}
                          onClick={handlePrint}
                          variant="gray"
                          icon={printIcon}
                          iconSize={18}
                        />
                        {!data?.archived && (
                          <CustomButton
                            title={t("archive")}
                            variant="red"
                            icon={archiveIcon}
                            iconSize={20}
                            onClick={() => setShowArchiveModal(true)}
                          />
                        )}
                      </>
                    ) : null}
                  </>
                )}
              </section>
            </>
          )}
        </div>
      </main>

      {showArchiveModal && (
        <Modal
          title={t("archiveInvoice")}
          onClose={handleCloseArchiveModal}
          footer={
            <>
              {loadingModal ? <Loader /> : null}
              <section className={classes.actions}>
                <CustomButton
                  onClick={handleArchive}
                  title={t("confirm")}
                  icon={saveIcon}
                  iconSize={16}
                  disabled={loadingModal}
                />
                <CustomButton
                  title={t("cancel")}
                  onClick={handleCloseArchiveModal}
                  variant="blank"
                  icon={closeIcon}
                  iconSize={14}
                />
              </section>
            </>
          }
        >
          <div className={classes.archiveModalBody}>
            <h3 className={classes.archiveModalBodyTitle}>{t("areYouSure")}</h3>
            <p className={classes.archiveModalInvoiceNumber}>
              {data?.invoiceNumber}
            </p>
          </div>
        </Modal>
      )}

      {showAreYouSure ? (
        <AreYouSureModal
          onClose={() => setShowAreYouSure(false)}
          onConfirm={cancelEdit}
          confirmIconType="whiteCheck"

        />
      ) : null}
    </>
  );
}
